/* Copyright (c) 2020-present CloudIO Inc */

import { Page, AnyItem, ItemType } from '../../registry/ComponentRegistry';
import { Platform } from '../../platform/Platform';
import { ActionManager, ActionManagerFn } from '../../registry/Component.type';
import { baseActionManager, baseActionProps } from '../../registry/Components';
import { defaultValue } from './ActionURLParam';

const ActionURLParamManager: ActionManagerFn<'ActionURLParam'> = (
  platform: Platform,
): ActionManager<'ActionURLParam'> => {
  return {
    ...baseActionManager(platform, 'ActionURLParam'),
    defaultValue,
    props: {
      ...baseActionProps,
      paramName: {
        label: 'Param Name',
        type: 'string',
        helpText:
          "Specify the name of the param to be appended to the URL. i.e. headerId will be appended as https://dev.cloudio.io?lineId=1&headerId=12345. Note: ? will be appended to the URL before appending the params if the URL doesn't already contains ?",
      },
      value: {
        label: 'Value',
        type: 'expression',
        returnType: async () => 'string',
      },
    },
    name: 'Set URL Param',
    getDisplayText: (item) => item.props.paramName,
    isAllowedInsideParent: (page: Page, parent: AnyItem) => {
      return (['ActionOpenURL'] as ItemType[]).includes(parent.type);
    },
    description: `**Overview**<br/>
The Set URL Param action is a specialized configuration component designed exclusively for use within the "Open URL" action. It functions as a structured builder for query string parameters, allowing developers to append data to an external URL without resorting to messy manual string concatenation.<br/><br/>
This component cannot exist independently; it is strictly an architectural child of ActionOpenURL. When added, it takes a specific key and a dynamic value and ensures they are properly formatted, encoded, and appended to the base URL (automatically handling the logic of whether to use ? or & separators).

**Key Features**<br/>
* **Safe Construction:** Eliminates the errors common in manual URL building (like forgetting a ? or improperly encoding special characters).
* **Strict Parent Scope:** Enforces usage validity via isAllowedInsideParent, ensuring it only functions as a direct child of ActionOpenURL.
* **Dynamic Values:** The value property supports expressions, enabling the parameter data to be calculated at runtime (e.g., passing the current record's ID).
* **Static Keys:** The paramName is a simple string, serving as the stable identifier for the query parameter.

**Properties**<br/>
* **Param Name (paramName):** A string property.
  * *Function:* The key of the query parameter (e.g., search or utm_source).
* **Value:** An expression property returning a string.
  * *Function:* The value assigned to the parameter.
  * *Example:* If Param Name is id and Value evaluates to 500, the resulting URL suffix is ?id=500 (or &id=500).

**Use Cases**<br/>
* **Search Integration:** When opening [https://google.com](https://google.com), adding a parameter with Param Name q and a Value bound to a text input field to perform a search.
* **External Reporting:** Linking to a third-party dashboard and passing startDate and endDate parameters to pre-filter the view.
* **Tracking:** Appending marketing parameters like utm_campaign=summer_sale when redirecting a user to a promotional landing page.`,
  };
};

export default ActionURLParamManager;
